import * as React from 'react';
import { camelCase, isEqual } from 'lodash';

import PieChart from 'components/graphs/echarts/pie-chart/PieChart';

import { getImpressionsPieChartData, preparePieChartData } from 'utils/charts/pie-chart';
import { COLORS, REPORTING_TASK_TYPE_ORDER, getReportingPieChartStyles } from 'utils/reporting';

import { abbrNum } from 'utils/helper/common';

import type { ChartFormatterParams, StatisticsCommon } from 'types';

import './campaign-impressions-pie-chart.scss';

type CampaignReportImpressions = {
  [x: string]: number
};

type Props = {
  impressions: CampaignReportImpressions,
  totalImpressions: number,
  emptyMessage?: string
};

const CampaignImpressionsPieChart = (
  {
    impressions,
    totalImpressions,
    emptyMessage
  }: Props
): JSX.Element => {
  const pieChartColors = [] as Array<string>;

  const data = REPORTING_TASK_TYPE_ORDER.reduce<Array<StatisticsCommon>>((res, type) => {
    const impressionsByType = impressions[camelCase(type)];
    const color = COLORS[type]?.main || '';

    if (impressionsByType) {
      res.push({
        name: type,
        label: type,
        absolute: impressionsByType,
        percentage: Math.round(impressionsByType / totalImpressions * 100),
        emphasis: { itemStyle: { color } }
      });

      pieChartColors.push(color);
    }

    return res;
  }, [] as Array<StatisticsCommon>);

  const handleFormatImpressions = (params: ChartFormatterParams): string => `{value|${abbrNum(params.value?.metric,
    { decimal: 1 }, true)}}\n{name|${params.name}} `;

  const pieChartData = preparePieChartData(getImpressionsPieChartData(data), 'audience', {
    formatter: handleFormatImpressions,
    colors: pieChartColors,
    styles: getReportingPieChartStyles()
  }, true);

  return (
    <PieChart
      id="campaign-impressions-pie-chart"
      options={pieChartData}
      hide={false}
      emptyMessage={emptyMessage}
    />
  );
};

export default React.memo(
  CampaignImpressionsPieChart,
  (prevProps, nextProps) => isEqual(prevProps, nextProps)
);
